import { useState, useEffect, useRef } from 'react';
import { LeadFollowups } from '../components/LeadFollowups';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Loader2, Trash2, Upload, Flame, UserPlus, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
    new: { label: 'New', color: '#3B82F6' },
    contacted: { label: 'Contacted', color: '#F59E0B' },
    registered: { label: 'Registered', color: '#8B5CF6' },
    deposited: { label: 'Deposited', color: '#22C55E' },
    not_interested: { label: 'Not interested', color: '#8A8FA3' },
};

const PITCH_EN = (name) =>
    `Hi ${name} 👋 It's Nikayo from Nikayo FX Guide. Thanks for your interest in learning forex the right way.\n\nStart here with your free lesson on market structure: https://nikayofxguide.com/learn\nThen create your free student account (no payment, no deposit needed): https://nikayofxguide.com/register\n\nI'll personally guide your first steps. Educational content only — not financial advice. GOD IS OUR BACKING 🙏`;
const PITCH_AR = (name) =>
    `مرحباً ${name} 👋 معك نيكايو من Nikayo FX Guide. شكراً لاهتمامك بتعلّم الفوركس بالطريقة الصحيحة.\n\nابدأ من هنا بدرسك المجاني عن هيكل السوق: https://nikayofxguide.com/learn\nثم أنشئ حسابك الطلابي المجاني (بدون دفع وبدون إيداع): https://nikayofxguide.com/register\n\nسأرشدك شخصياً في خطواتك الأولى. محتوى تعليمي فقط — ليس نصيحة مالية. الله هو سندنا 🙏`;

const waLink = (lead) => {
    const num = (lead.phone || '').replace(/[^\d]/g, '');
    const firstName = (lead.name || '').split(' ')[0] || 'there';
    const text = lead.language === 'ar' ? PITCH_AR(firstName) : PITCH_EN(firstName);
    return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
};

export default function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const fetchAll = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/leads`, { withCredentials: true });
            setLeads(data.leads);
            setStats(data.stats);
        } catch (e) {
            toast.error('Failed to load leads');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await axios.post(`${API}/admin/leads/import`, fd, { withCredentials: true });
            toast.success(`Imported ${data.imported} new lead${data.imported === 1 ? '' : 's'}${data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicates skipped` : ''}`);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Import failed — make sure it is the CSV from Meta');
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const setStatus = async (id, status) => {
        setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
        try {
            await axios.patch(`${API}/admin/leads/${id}`, { status }, { withCredentials: true });
            fetchAll();
        } catch (e) {
            toast.error('Failed to update status');
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this lead permanently?')) return;
        try {
            await axios.delete(`${API}/admin/leads/${id}`, { withCredentials: true });
            setLeads((ls) => ls.filter((l) => l.id !== id));
            toast.success('Lead deleted');
        } catch (e) {
            toast.error('Failed to delete');
        }
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            <main className="max-w-6xl mx-auto px-4 py-10">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3">
                        <UserPlus className="w-6 h-6" style={{ color: 'var(--primary-color)' }} />
                        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>Ad Leads</h1>
                    </div>
                    <div>
                        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onUpload} data-testid="leads-file-input" />
                        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-leads-btn">
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                            Upload Meta CSV
                        </Button>
                    </div>
                </div>
                <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                    Download leads from Meta (Ads Manager → your Leads ad → View leads → Download CSV), upload here, then message each one on WhatsApp with one tap. Duplicates are skipped automatically.
                </p>

                <LeadFollowups refreshKey={leads.length} onChanged={fetchAll} />

                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="leads-stats">
                        {[
                            ['Total', stats.total, 'var(--text-primary)'],
                            ['🔥 Hot', stats.hot, '#FFB800'],
                            ['New', stats.new, STATUS_META.new.color],
                            ['Contacted', stats.contacted, STATUS_META.contacted.color],
                            ['Registered', stats.registered, STATUS_META.registered.color],
                            ['Deposited', stats.deposited, STATUS_META.deposited.color],
                        ].map(([label, val, color]) => (
                            <div key={label} className="card-surface p-4 text-center" data-testid={`leads-stat-${label.replace('🔥 ', '').toLowerCase()}`}>
                                <p className="text-2xl font-black" style={{ color }}>{val}</p>
                                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
                            </div>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--primary-color)' }} /></div>
                ) : leads.length === 0 ? (
                    <div className="card-surface p-10 text-center" data-testid="no-leads">
                        <p className="font-bold mb-2" style={{ color: 'var(--text-primary)' }}>No leads yet</p>
                        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                            Run your Meta Leads campaign (Instant Forms), download the CSV of responses, and upload it with the button above.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="leads-list">
                        {leads.map((l) => (
                            <div key={l.id} className="card-surface p-4 flex flex-wrap items-center gap-3" data-testid={`lead-row-${l.id}`}
                                 style={l.is_hot ? { borderColor: 'rgba(255,184,0,0.45)' } : undefined}>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{l.name}</span>
                                        {l.is_hot && (
                                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black" style={{ backgroundColor: 'rgba(255,184,0,0.12)', color: '#FFB800', border: '1px solid rgba(255,184,0,0.35)' }}>
                                                <Flame className="w-3 h-3" /> HOT
                                            </span>
                                        )}
                                        {l.language === 'ar' && (
                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: 'var(--primary-muted)', color: 'var(--primary-color)' }}>AR</span>
                                        )}
                                    </div>
                                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                        {l.phone}{l.email ? ` · ${l.email}` : ''}
                                    </p>
                                    {l.answer && <p className="text-xs mt-1 truncate" style={{ color: 'var(--text-secondary)' }}>{l.answer}</p>}
                                </div>
                                <select
                                    value={l.status}
                                    onChange={(e) => setStatus(l.id, e.target.value)}
                                    className="rounded-lg px-2 py-1.5 text-xs font-bold outline-none cursor-pointer"
                                    style={{ backgroundColor: 'var(--background-elevated)', color: STATUS_META[l.status]?.color, border: '1px solid var(--border-subtle)' }}
                                    data-testid={`lead-status-${l.id}`}
                                >
                                    {Object.entries(STATUS_META).map(([val, m]) => (
                                        <option key={val} value={val}>{m.label}</option>
                                    ))}
                                </select>
                                {l.phone && (
                                    <a href={waLink(l)} target="_blank" rel="noopener noreferrer" data-testid={`lead-whatsapp-${l.id}`}>
                                        <Button size="sm" style={{ backgroundColor: '#25D366', color: '#fff' }}>
                                            <MessageCircle className="w-4 h-4 mr-1.5" /> Message
                                        </Button>
                                    </a>
                                )}
                                <Button size="sm" variant="outline" onClick={() => remove(l.id)} style={{ color: '#EF4444' }} data-testid={`delete-lead-${l.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
